import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import { toPattern } from "vanilla-masker";
import { Info } from "lucide-react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";
import { FormSection, FormSectionTitle, SectionDescription } from "./styles";
import { IRequestDossierInputModel } from "inputModels";
import { Gender } from "types/enums";
import { MASK_CEP, MASK_PROCESS_NUMBER } from "../../constants";
import dayjs, { Dayjs } from "dayjs";

interface ComplementaryDataFormProps {
  requiredParams: string[];
  onValidChange: (isValid: boolean) => void;
  onDataChange: (data: Partial<IRequestDossierInputModel>) => void;
}

const ComplementaryDataForm = ({
  requiredParams,
  onValidChange,
  onDataChange,
}: ComplementaryDataFormProps) => {
  const paramNameToFormFieldNameMap: Record<
    string,
    keyof IRequestDossierInputModel
  > = {
    process_number: "processNumber",
    cep: "cep",
    address: "address",
    neighborhood: "neighborhood",
    city: "city",
    uf: "federativeUnit",
    name: "name",
    identity_number: "identityNumber",
    expedition_organ: "expeditionOrgan",
    mother_name: "motherName",
    father_name: "fatherName",
    birth_date: "birthDate",
    gender: "gender",
    key_words: "keywords",
  };

  const formFieldRequiredParams = requiredParams
    .map((param) => paramNameToFormFieldNameMap[param])
    .filter(Boolean) as (keyof IRequestDossierInputModel)[];

  const {
    control,
    formState: { errors, isValid },
    watch,
    getValues,
  } = useForm<IRequestDossierInputModel>({
    defaultValues: {
      name: "",
      motherName: "",
      fatherName: "",
      processNumber: "",
      federativeUnit: "",
      expeditionOrgan: "",
      identityNumber: "",
      birthDate: undefined,
      keywords: [],
      gender: undefined,
      city: "",
      neighborhood: "",
      cep: "",
      address: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);

  useEffect(() => {
    const subscription = watch(() => {
      const data = Object.fromEntries(
        Object.entries(getValues()).filter(
          ([key, val]) =>
            formFieldRequiredParams.includes(
              key as keyof IRequestDossierInputModel
            ) &&
            val !== undefined &&
            val !== "" &&
            !(Array.isArray(val) && val.length === 0)
        )
      ) as Partial<IRequestDossierInputModel>;
      onDataChange(data);
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues, onDataChange, formFieldRequiredParams]);

  const getValidationRules = (field: keyof IRequestDossierInputModel) => {
    const rules: any = {};
    if (formFieldRequiredParams.includes(field)) {
      rules.required = `Campo obrigatório`;
      if (field === "cep") {
        rules.validate = (value: string | undefined) => {
          if (!value) return "Campo obrigatório";
          const digits = value.replace(/[^\d]/g, "");
          if (digits.length !== 8) {
            return "CEP deve conter 8 dígitos";
          }
          return true;
        };
      }
      if (field === "processNumber") {
        rules.validate = (value: string | undefined) => {
          if (!value) return "Campo obrigatório";
          if (value.length !== 20) {
            return "Número de processo deve conter 20 dígitos";
          }
          return true;
        };
      }
      if (field === "birthDate") {
        rules.validate = (value: Date | undefined) => {
          if (!value) return "Campo obrigatório";
          if (value > new Date()) {
            return "Data não pode ser futura";
          }
          return true;
        };
      }
      if (field === "expeditionOrgan") {
        rules.pattern = {
          value: /^[A-Za-z\s]+$/,
          message: "Órgão expedidor deve conter apenas letras",
        };
      }
      if (field === "keywords") {
        rules.validate = (value: string[] | undefined) =>
          !value || value.length > 0 || "Informe ao menos uma palavra-chave";
      }
    }
    return rules;
  };

  const noFieldsRequired = formFieldRequiredParams.length === 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Typography variant="h6">Dados Complementares</Typography>
      {noFieldsRequired ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="body1" color="textSecondary">
            Nenhum dado complementar é necessário para gerar o dossiê.
          </Typography>
        </Box>
      ) : (
        <>
          <SectionDescription>
            Preencha os dados adicionais necessários para as fontes selecionadas
          </SectionDescription>

          {(formFieldRequiredParams.includes("name") ||
            formFieldRequiredParams.includes("motherName") ||
            formFieldRequiredParams.includes("fatherName") ||
            formFieldRequiredParams.includes("birthDate")) && (
            <FormSection>
              <FormSectionTitle>Dados Pessoais</FormSectionTitle>
              <Grid container spacing={2}>
                {formFieldRequiredParams.includes("name") && (
                  <Grid item xs={12}>
                    <Controller
                      name="name"
                      control={control}
                      rules={getValidationRules("name")}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Nome"
                          placeholder="Nome completo"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          {...field}
                          //teste
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("birthDate") && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="birthDate"
                      control={control}
                      rules={getValidationRules("birthDate")}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de nascimento"
                          format="DD/MM/YYYY"
                          value={field.value ? dayjs(field.value) : undefined}
                          onChange={(date: Dayjs | null) => {
                            field.onChange(date ? date.toDate() : undefined);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: !!errors.birthDate,
                              helperText: errors.birthDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("motherName") && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="motherName"
                      control={control}
                      rules={getValidationRules("motherName")}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Nome da mãe"
                          placeholder="Nome da mãe"
                          error={!!errors.motherName}
                          helperText={errors.motherName?.message}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("fatherName") && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="fatherName"
                      control={control}
                      rules={getValidationRules("fatherName")}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Nome do pai"
                          placeholder="Nome do pai"
                          error={!!errors.fatherName}
                          helperText={errors.fatherName?.message}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </FormSection>
          )}

          {(formFieldRequiredParams.includes("identityNumber") ||
            formFieldRequiredParams.includes("expeditionOrgan") ||
            formFieldRequiredParams.includes("gender")) && (
            <FormSection>
              <FormSectionTitle>Documentação</FormSectionTitle>
              <Grid container spacing={2}>
                {formFieldRequiredParams.includes("identityNumber") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="identityNumber"
                      control={control}
                      rules={getValidationRules("identityNumber")}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <TextField
                          fullWidth
                          size="small"
                          value={value || ""}
                          onChange={(e) => {
                            const input = e.target.value.toUpperCase();
                            const formatted = input.replace(/[^A-Z0-9.-]/g, "");
                            onChange(formatted);
                          }}
                          label="Número de identidade"
                          placeholder="Ex: UF-00.000.000 ou 00.000.000-0"
                          error={!!errors.identityNumber}
                          helperText={errors.identityNumber?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("expeditionOrgan") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="expeditionOrgan"
                      control={control}
                      rules={getValidationRules("expeditionOrgan")}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Órgão expedidor"
                          placeholder="Órgão Expedidor"
                          value={value || ""}
                          onChange={(e) => {
                            const filteredValue = e.target.value.replace(
                              /[^A-Za-z\s]/g,
                              ""
                            );
                            onChange(filteredValue);
                          }}
                          error={!!errors.expeditionOrgan}
                          helperText={errors.expeditionOrgan?.message}
                          {...rest}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("gender") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="gender"
                      control={control}
                      rules={getValidationRules("gender")}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.gender}
                        >
                          <InputLabel id="gender-label">Gênero</InputLabel>
                          <Select
                            labelId="gender-label"
                            label="Gênero"
                            {...field}
                            value={field.value !== undefined ? field.value : ""}
                          >
                            <MenuItem value="">Selecione</MenuItem>
                            <MenuItem value={Gender.MALE}>Masculino</MenuItem>
                            <MenuItem value={Gender.FEMALE}>Feminino</MenuItem>
                          </Select>
                          {errors.gender && (
                            <Typography variant="caption" color="error">
                              {errors.gender?.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </FormSection>
          )}

          {formFieldRequiredParams.includes("processNumber") && (
            <FormSection>
              <FormSectionTitle>Busca de Processos</FormSectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="processNumber"
                    control={control}
                    rules={getValidationRules("processNumber")}
                    render={({ field: { onChange, value, ...rest } }) => (
                      <TextField
                        fullWidth
                        size="small"
                        label="Número de processo"
                        placeholder="0000000-00.0000.0.00.0000"
                        value={toPattern(
                          String(value || ""),
                          MASK_PROCESS_NUMBER
                        )}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/[^\d]/g, "");
                          onChange(rawValue);
                        }}
                        slotProps={{
                          htmlInput: {
                            maxLength: 25,
                          },
                        }}
                        error={!!errors.processNumber}
                        helperText={errors.processNumber?.message}
                        InputProps={{
                          endAdornment: (
                            <Tooltip
                              data-testid="tooltipCDA"
                              title={
                                <h1 style={{ fontSize: "1rem" }}>
                                  O Número de Processo deve ser informado para
                                  que fonte Busca por licitações no Portal da
                                  Transparência faça a busca corretamente.
                                </h1>
                              }
                              placement="top"
                              arrow
                            >
                              <Info
                                size={16}
                                color="#293778"
                                style={{ marginLeft: 8 }}
                              />
                            </Tooltip>
                          ),
                        }}
                        {...rest}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}

          {(formFieldRequiredParams.includes("address") ||
            formFieldRequiredParams.includes("neighborhood") ||
            formFieldRequiredParams.includes("city") ||
            formFieldRequiredParams.includes("cep") ||
            formFieldRequiredParams.includes("federativeUnit")) && (
            <FormSection>
              <FormSectionTitle>Dados de Endereço</FormSectionTitle>
              <Grid container spacing={2}>
                {formFieldRequiredParams.includes("address") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="address"
                      control={control}
                      rules={getValidationRules("address")}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Logradouro"
                          placeholder="Logradouro"
                          error={!!errors.address}
                          helperText={errors.address?.message}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("neighborhood") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="neighborhood"
                      control={control}
                      rules={getValidationRules("neighborhood")}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Bairro"
                          placeholder="Bairro"
                          error={!!errors.neighborhood}
                          helperText={errors.neighborhood?.message}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("city") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="city"
                      control={control}
                      rules={getValidationRules("city")}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="Cidade"
                          placeholder="Cidade"
                          error={!!errors.city}
                          helperText={errors.city?.message}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("cep") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="cep"
                      control={control}
                      rules={getValidationRules("cep")}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <TextField
                          fullWidth
                          size="small"
                          label="CEP"
                          placeholder="00000-000"
                          value={toPattern(String(value || ""), MASK_CEP)}
                          onChange={(e) => onChange(e.target.value)}
                          error={!!errors.cep}
                          helperText={errors.cep?.message}
                          {...rest}
                        />
                      )}
                    />
                  </Grid>
                )}
                {formFieldRequiredParams.includes("federativeUnit") && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="federativeUnit"
                      control={control}
                      rules={getValidationRules("federativeUnit")}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.federativeUnit}
                        >
                          <InputLabel id="uf-label">UF</InputLabel>
                          <Select
                            labelId="uf-label"
                            label="UF"
                            {...field}
                            value={field.value || ""}
                          >
                            <MenuItem value="">Selecione</MenuItem>
                            {[
                              "AC",
                              "AL",
                              "AP",
                              "AM",
                              "BA",
                              "CE",
                              "DF",
                              "ES",
                              "GO",
                              "MA",
                              "MT",
                              "MS",
                              "MG",
                              "PA",
                              "PB",
                              "PR",
                              "PE",
                              "PI",
                              "RJ",
                              "RN",
                              "RS",
                              "RO",
                              "RR",
                              "SC",
                              "SP",
                              "SE",
                              "TO",
                            ].map((uf) => (
                              <MenuItem key={uf} value={uf}>
                                {uf}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.federativeUnit && (
                            <Typography variant="caption" color="error">
                              {errors.federativeUnit?.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </FormSection>
          )}

          {formFieldRequiredParams.includes("keywords") && (
            <FormSection>
              <FormSectionTitle>Palavras-chave</FormSectionTitle>
              <Controller
                name="keywords"
                control={control}
                rules={getValidationRules("keywords")}
                render={({ field: { onChange, value, ...rest } }) => (
                  <TextField
                    fullWidth
                    size="small"
                    label="Palavras-chave"
                    placeholder="Informe as palavras-chave separadas por vírgula"
                    value={value?.join(", ") || ""}
                    onChange={(e) =>
                      onChange(
                        e.target.value
                          ? e.target.value.split(",").map((k) => k.trim())
                          : []
                      )
                    }
                    error={!!errors.keywords}
                    helperText={errors.keywords?.message}
                    {...rest}
                  />
                )}
              />
            </FormSection>
          )}
        </>
      )}
    </LocalizationProvider>
  );
};

export default ComplementaryDataForm;
